import type { ApiClient } from './runtime/apiRuntime';
import { apiMutationOptions, BaseApiMutationOptions } from './apiMutationOptions';
import { apiQueryOptions, BaseApiQueryOptions } from './apiQueryOptions';
import { SESSION_QUERY_KEY } from './runtime/identity';

/**
 * How the session query is observed. The observer in the AuthProvider and the ensure call in the
 * root beforeLoad must use exactly these options, or they configure the same entry differently.
 */
export const SESSION_QUERY_OPTIONS: BaseApiQueryOptions = {
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 30 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000
};

export const LoginApi = {
    register: (options?: BaseApiMutationOptions<ApiClient['login']['register']>) => apiMutationOptions((api) => api.login.register, options),
    exists: (options?: BaseApiMutationOptions<ApiClient['login']['exists']>) => apiMutationOptions((api) => api.login.exists, options),
    login: (options?: BaseApiMutationOptions<ApiClient['login']['login']>) => apiMutationOptions((api) => api.login.login, options),
    logout: (options?: BaseApiMutationOptions<ApiClient['login']['logout']>) => apiMutationOptions((api) => api.login.logout, options),
    refreshToken: (options?: BaseApiQueryOptions) => apiQueryOptions(['login', 'refreshToken'], (api) => api.login.refreshToken(), options),
    me: (options?: BaseApiQueryOptions) => apiQueryOptions(['login', 'me'], (api) => api.login.me(), options),

    /**
     * The current session, fetched the right way for wherever this runs.
     *
     * Both endpoints answer with the same payload; /me simply leaves the token empty. Server-side
     * rendering uses it because its query result is serialised into the page it returns, where any
     * script could read a token. The browser calls /refresh, so the token reaches it over the wire
     * and the AuthSession cookie gets renewed - which only a response that actually arrives at the
     * browser can do.
     *
     * One query key on purpose, and identity-independent (see runtime/identity.ts): the SSR result
     * has to hydrate into the client's cache, and the scope of every other key is read from this
     * entry. Pair this with refetchOnMount: 'always', or the client keeps the tokenless SSR answer
     * until it goes stale and has nothing to authenticate with in the meantime.
     */
    session: (options?: BaseApiQueryOptions) =>
        apiQueryOptions(SESSION_QUERY_KEY, (api) => (import.meta.env.SSR ? api.login.me() : api.login.refreshToken()), options),
    forgotPassword: (options?: BaseApiMutationOptions<ApiClient['login']['forgotPassword']>) => apiMutationOptions((api) => api.login.forgotPassword, options),
    resetPassword: (options?: BaseApiMutationOptions<ApiClient['login']['resetPassword']>) => apiMutationOptions((api) => api.login.resetPassword, options),
    changePassword: (options?: BaseApiMutationOptions<ApiClient['login']['changePassword']>) => apiMutationOptions((api) => api.login.changePassword, options)
};
